import { Suspense } from 'react';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Store } from 'redux';

import { logCritical } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { APP_NAMES } from '@savgroup-front-common/constants';
import { FingerPrint } from '@savgroup-front-common/core/atoms/FingerPrint';
import { ToastContainer } from '@savgroup-front-common/core/atoms/Toast';
import { TooltipProvider } from '@savgroup-front-common/core/atoms/Tooltip/Tooltip';
import { ConfirmationContextProvider } from '@savgroup-front-common/core/components/Confirmation/ConfirmationContextProvider';
import { GraphQlSseContextProvider } from '@savgroup-front-common/core/components/GraphqlSseContext/GraphqlSseContext';
import { HotjarProvider } from '@savgroup-front-common/core/components/HotjarProvider';
import IntlManager from '@savgroup-front-common/core/components/IntlManager/IntlManager';
import { NewUiProvider } from '@savgroup-front-common/core/components/NewUiProvider/NewUiProvider';
import { GlobalStyle } from '@savgroup-front-common/core/helpers';
import { useAppendNoIndexMeta } from '@savgroup-front-common/core/hooks';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { NetworkStatusProvider } from '@savgroup-front-common/core/molecules/NetworkStatusProvider/NetworkStatusProvider';
import {
  BannersProvider,
  ToastsProvider,
} from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { ExternalPortal } from '@savgroup-front-common/core/protons/ExternalPortal/ExternalPortal';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import { HOTJAR_CONFIG } from '@/configuration';

import { QrcodeUploadPage, queryClient } from './App.config';
import { useInitGoogleMap } from './hooks';
import { AuthProvider } from './login/AuthProviderContext';
import CheckAuthorized from './login/CheckAuthorized';
import { Callback } from './login/OAuthCallback';
import ThemeContainer from './ThemeContainer';

const shouldEnableReactQueryDevtools =
  import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === 'true';

interface AppProps {
  // App only forwards the store to the Redux provider and never reads its
  // state, so the concrete root-state shape is intentionally not constrained
  // here (the store is still assembled by the untyped JS `configureStore`).
  store: Store;
}

export default function App({ store }: AppProps) {
  const { isLoading } = useInitGoogleMap();

  useAppendNoIndexMeta({ VITE_ENV: import.meta.env.VITE_ENV as string });

  if (isLoading) {
    return null;
  }

  try {
    return (
      <NewUiProvider>
        <HotjarProvider config={HOTJAR_CONFIG}>
          <ToastsProvider>
            <NetworkStatusProvider>
              <TooltipProvider>
                <GlobalStyle myAccount />
                <ReduxProvider store={store}>
                  <QueryClientProvider client={queryClient}>
                    {shouldEnableReactQueryDevtools && (
                      <ReactQueryDevtools initialIsOpen={false} />
                    )}
                    <BannersProvider>
                      <AuthProvider>
                        <ThemeContainer>
                          <IntlManager appName={APP_NAMES.SELFCARE}>
                            <FingerPrint>
                              <ConfirmationContextProvider>
                                <Suspense fallback={<BaseLoader />}>
                                  <ExternalPortal>
                                    <ToastContainer topOffset={rem(84)} />
                                  </ExternalPortal>

                                  <GraphQlSseContextProvider>
                                    <BrowserRouter>
                                      <Routes>
                                        <Route
                                          path="/callback"
                                          element={<Callback />}
                                        />

                                        <Route
                                          path="qrcodeUpload/:token"
                                          element={
                                            <Suspense fallback={<BaseLoader />}>
                                              <QrcodeUploadPage />
                                            </Suspense>
                                          }
                                        />

                                        <Route
                                          path="*"
                                          element={<CheckAuthorized />}
                                        />
                                      </Routes>
                                    </BrowserRouter>
                                  </GraphQlSseContextProvider>
                                </Suspense>
                              </ConfirmationContextProvider>
                            </FingerPrint>
                          </IntlManager>
                        </ThemeContainer>
                      </AuthProvider>
                    </BannersProvider>
                  </QueryClientProvider>
                </ReduxProvider>
              </TooltipProvider>
            </NetworkStatusProvider>
          </ToastsProvider>
        </HotjarProvider>
      </NewUiProvider>
    );
  } catch (err) {
    logCritical(err);
    throw err;
  }
}
